import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Calendar, MessageSquare, Phone, Users, type LucideIcon } from "lucide-react";
import { formatActivityDateTime } from "@/components/bookings/formatActivityDateTime";
import { Card, TruncatedText, focusRingInsetClass } from "@/components/ui";
import { BookingStatusBadge } from "./BookingStatusBadge";
import { BookingCancellationSource } from "./BookingCancellationSource";
import { BookingLineItemChips } from "./BookingLineItemChips";
import { BookingNotesPreview } from "./BookingNotesPreview";
import { PartnerBookingMobileBody } from "./PartnerBookingMobileBody";
import { PartnerReceiptActions } from "./PartnerReceiptActions";
import {
  partnerBookingMobileChrome,
  partnerBookingStatusBorderWidth,
  partnerHighlightCardClass,
} from "./partnerBookingChrome";
import {
  getPartnerBookingCardPresentation,
  getPartnerVisitDateTimeLabels,
} from "./partnerBookingCardPresentation";
import { getBookingAppearance } from "./bookingAppearance";
import {
  showPartnerReceiptSection,
  getUpfrontPaidCents,
} from "./sellerBookingRules";
import { UpfrontPaidDisplay } from "./UpfrontPaidDisplay";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";
import { bookingPartyCount } from "./bookingPartyCount";

export type BookingViewer = "customer" | "partner" | "staff";

interface BookingCardProps {
  booking: BookingListRow;
  detailPath: string;
  viewer: BookingViewer;
  isProcessingReceipt: boolean;
  fileInputResetKey: number;
  onReceiptUpload: (file: File) => void;
  onManualAmountClick?: () => void;
  onEditSellerNotes?: (bookingId: string, currentNotes: string | null) => void;
  layout?: "row" | "grid";
  /** Hide visit date when the surrounding day section already shows it. */
  omitVisitDate?: boolean;
}

function SecondaryMeta({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: string;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-surface-500">
      <Icon className="h-3 w-3 shrink-0 text-surface-400" aria-hidden />
      <TruncatedText>{children}</TruncatedText>
    </span>
  );
}

export function BookingCard({
  booking,
  detailPath,
  viewer,
  isProcessingReceipt,
  fileInputResetKey,
  onReceiptUpload,
  onManualAmountClick,
  onEditSellerNotes,
  layout = "row",
  omitVisitDate = false,
}: BookingCardProps) {
  const { t } = useTranslation("bookings");
  const isGrid = layout === "grid";
  const isCustomer = viewer === "customer";
  const isPartner = viewer === "partner";

  const phone = booking.contactInfo.phone?.trim() || null;
  const showReceiptArea =
    !isCustomer &&
    (isPartner ? showPartnerReceiptSection(booking) : booking.isReceiptBased);
  const upfrontPaidCents = isPartner ? getUpfrontPaidCents(booking) : null;

  const cardTitle = isPartner
    ? booking.customerDisplayName
    : booking.activityName || t("unknownActivity");

  const { Icon: BookingIcon, bgColor, color } = getBookingAppearance(
    booking,
    isPartner,
  );

  const visitLabels = isPartner
    ? getPartnerVisitDateTimeLabels(booking)
    : booking.visitAt
      ? formatActivityDateTime(
          booking.visitAt,
          booking.activityRegionCode,
          (time) => t("adminDetail.localTime", { time }),
        )
      : null;

  const timeLabel =
    visitLabels?.timeLabel ?? booking.timeSlot?.trim() ?? null;

  const partnerTone = isPartner
    ? getPartnerBookingCardPresentation(booking)
    : null;
  const statusAccent = partnerTone?.statusAccent;
  const titleMuted = partnerTone?.titleMuted ?? false;
  const highlightCard = partnerTone?.highlightCard ?? false;

  const showLineItems = isPartner && booking.lineItems.length > 0;
  const activitySubtitle =
    isPartner && booking.activityName?.trim()
      ? booking.activityName
      : null;
  const partyCount = bookingPartyCount(booking);

  const receiptActionContent = showReceiptArea ? (
    <PartnerReceiptActions
      booking={booking}
      isProcessingReceipt={isProcessingReceipt}
      fileInputResetKey={fileInputResetKey}
      onReceiptUpload={onReceiptUpload}
      onManualAmountClick={onManualAmountClick}
    />
  ) : null;

  const receiptActions = receiptActionContent ? (
    <div
      className={
        isGrid
          ? "border-t border-surface-200/70 bg-surface-50/60 px-4 py-3 dark:bg-surface-100/30"
          : "flex shrink-0 flex-col items-stretch justify-center gap-2 border-t border-surface-200/70 bg-surface-50/60 px-4 py-3 sm:self-stretch sm:border-l sm:border-t-0 sm:px-4 dark:bg-surface-100/30"
      }
      onClick={(e) => e.stopPropagation()}
    >
      {receiptActionContent}
    </div>
  ) : null;

  const statusBlock = (
    <div className="flex flex-wrap items-center gap-1.5">
      <BookingStatusBadge
        status={booking.status}
        handshakeStatus={booking.handshakeStatus}
        partnerView={isPartner}
        size="sm"
      />
      {booking.status === "CANCELED" && booking.cancellationSource ? (
        <BookingCancellationSource source={booking.cancellationSource} />
      ) : null}
      {upfrontPaidCents != null ? (
        <UpfrontPaidDisplay amountCents={upfrontPaidCents} />
      ) : null}
    </div>
  );

  const titleBlock = (
    <div className="min-w-0 space-y-0.5">
      <h3
        className={cn(
          "min-w-0 font-semibold tracking-tight",
          isGrid ? "text-sm leading-snug" : "text-base leading-snug",
          titleMuted ? "text-surface-500" : "text-surface-900",
        )}
      >
        {cardTitle}
      </h3>
      {activitySubtitle ? (
        <TruncatedText as="p" className="text-xs text-surface-500">
          {activitySubtitle}
        </TruncatedText>
      ) : null}
    </div>
  );

  // Guest meta when there are no line-item chips (chips already show quantities).
  const coversLabel =
    partyCount > 0 && !showLineItems
      ? t("guest", { count: partyCount })
      : null;

  const secondaryRow = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {coversLabel ? (
        <SecondaryMeta icon={Users}>{coversLabel}</SecondaryMeta>
      ) : null}
      {!omitVisitDate && visitLabels?.dateLabel ? (
        <SecondaryMeta icon={Calendar}>{visitLabels.dateLabel}</SecondaryMeta>
      ) : null}
      {!isCustomer && phone ? (
        <SecondaryMeta icon={Phone}>{phone}</SecondaryMeta>
      ) : null}
      {viewer === "staff" && booking.contactInfo.email ? (
        <TruncatedText className="text-xs text-surface-500">
          {booking.contactInfo.email}
        </TruncatedText>
      ) : null}
      {!isGrid && !isPartner && booking.kioskName ? (
        <span
          className="text-xs text-surface-400"
          title={t("kioskBookingTitle")}
        >
          {booking.kioskName}
        </span>
      ) : null}
      {isPartner &&
      booking.activityRequiresHandshake &&
      booking.handshakeSessionId ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
          <MessageSquare className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t("handshake.messageCount", {
            count: booking.handshakeMessageCount,
          })}
        </span>
      ) : null}
    </div>
  );

  const notes = isPartner ? (
    <BookingNotesPreview
      booking={booking}
      onEditSellerNotes={onEditSellerNotes}
    />
  ) : null;

  const typeIcon = (
    <div
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
        bgColor,
      )}
    >
      <BookingIcon className={cn("h-4 w-4", color)} aria-hidden />
    </div>
  );

  const timeRail = timeLabel ? (
    <div
      className="flex w-[3.25rem] shrink-0 flex-col items-start pt-0.5 sm:w-[4.5rem]"
      aria-label={timeLabel}
    >
      <span
        className={cn(
          "text-lg font-semibold tabular-nums leading-none tracking-tight sm:text-xl",
          titleMuted ? "text-surface-400" : "text-surface-900",
        )}
      >
        {timeLabel}
      </span>
    </div>
  ) : null;

  const body = (
    <>
      {showLineItems ? (
        <BookingLineItemChips items={booking.lineItems} />
      ) : null}
      {!isPartner && booking.serviceName ? (
        <p className="text-sm text-surface-600">{booking.serviceName}</p>
      ) : null}
      {secondaryRow}
      {notes}
    </>
  );

  const cardShellClass = cn(
    "overflow-hidden transition-all duration-200",
    isPartner && partnerBookingStatusBorderWidth,
    isPartner && !isGrid && partnerBookingMobileChrome,
    statusAccent,
    highlightCard && partnerHighlightCardClass,
    titleMuted && "bg-surface-100/80",
    "hover:border-surface-300 sm:hover:-translate-y-px sm:hover:shadow-elevated",
  );

  const linkHoverClass = cn(
    "transition-colors hover:bg-surface-50/70 dark:hover:bg-surface-100/40",
    focusRingInsetClass,
  );

  if (isGrid) {
    return (
      <Card className={cn("flex h-full flex-col", cardShellClass)}>
        <Link
          to={detailPath}
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-3 p-4 outline-none",
            linkHoverClass,
          )}
        >
          <div className="flex items-start justify-between gap-3">
            {timeLabel ? (
              <span
                className={cn(
                  "text-xl font-semibold tabular-nums leading-none tracking-tight",
                  titleMuted ? "text-surface-400" : "text-surface-900",
                )}
              >
                {timeLabel}
              </span>
            ) : (
              typeIcon
            )}
            {statusBlock}
          </div>
          {titleBlock}
          {body}
        </Link>
        {receiptActions}
      </Card>
    );
  }

  if (isPartner) {
    const paidAmount =
      upfrontPaidCents != null ? (
        <UpfrontPaidDisplay
          amountCents={upfrontPaidCents}
          className="text-sm font-semibold text-surface-800"
        />
      ) : null;

    return (
      <Card className={cardShellClass}>
        <Link
          to={detailPath}
          className={cn("block outline-none sm:hidden", linkHoverClass)}
        >
          <PartnerBookingMobileBody
            booking={booking}
            omitVisitDate={omitVisitDate}
            titleMuted={titleMuted}
            visitDateLabel={visitLabels?.dateLabel ?? null}
            visitTimeLabel={timeLabel}
            statusBadge={
              <BookingStatusBadge
                status={booking.status}
                handshakeStatus={booking.handshakeStatus}
                partnerView
                size="sm"
              />
            }
            paidAmount={paidAmount}
            cancellationSource={
              booking.status === "CANCELED" && booking.cancellationSource ? (
                <BookingCancellationSource source={booking.cancellationSource} />
              ) : null
            }
            receiptActions={
              showReceiptArea ? (
                <PartnerReceiptActions
                  booking={booking}
                  isProcessingReceipt={isProcessingReceipt}
                  fileInputResetKey={fileInputResetKey}
                  onReceiptUpload={onReceiptUpload}
                  onManualAmountClick={onManualAmountClick}
                  fullWidth
                />
              ) : undefined
            }
          >
            {secondaryRow}
            {notes}
          </PartnerBookingMobileBody>
        </Link>

        <div className="hidden sm:flex sm:flex-row sm:items-stretch">
          <Link
            to={detailPath}
            className={cn(
              "flex min-w-0 flex-1 gap-3 p-3.5 outline-none sm:gap-4 sm:px-4 sm:py-3.5",
              linkHoverClass,
            )}
          >
            {timeRail ?? typeIcon}
            <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                {titleBlock}
                <div className="shrink-0">{statusBlock}</div>
              </div>
              {body}
            </div>
          </Link>
          {receiptActions}
        </div>
      </Card>
    );
  }

  return (
    <Card className={cardShellClass}>
      <div className="flex flex-col sm:flex-row sm:items-stretch">
        <Link
          to={detailPath}
          className={cn(
            "flex min-w-0 flex-1 gap-3 p-3.5 outline-none sm:gap-4 sm:px-4 sm:py-3.5",
            linkHoverClass,
          )}
        >
          {timeRail ?? typeIcon}
          <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
            <div className="flex items-start justify-between gap-2 sm:gap-3">
              {titleBlock}
              <div className="shrink-0">{statusBlock}</div>
            </div>
            {body}
          </div>
        </Link>
        {receiptActions}
      </div>
    </Card>
  );
}
